import { useMemo } from "react"
import { AgGridReact } from "ag-grid-react"
import {
  AllCommunityModule,
  ModuleRegistry,
  type ColDef,
  type GridOptions,
  type GridReadyEvent,
  type IDatasource,
  type RowModelType,
} from "ag-grid-community"
import { useTheme } from "../../store/themeStore"
import { useAuthStore } from "../../store/authStore"
import { agDarkTheme, agLightTheme } from "../../lib/agGridTheme"
import { commonDefaultColDef, commonGridOptions } from "../../lib/agGridOptions"
import { withAuditColumns } from "../../lib/auditColumnDefs"
import { cn } from "../../lib/utils"

ModuleRegistry.registerModules([AllCommunityModule])

export type DataGridProps<TData = unknown> = {
  rowData?: TData[]
  columnDefs: ColDef<TData>[]
  className?: string
  height?: number | string
  loading?: boolean
  context?: Record<string, unknown>
  defaultColDef?: ColDef<TData>
  gridOptions?: GridOptions<TData>
  onGridReady?: (event: GridReadyEvent<TData>) => void
  pagination?: boolean
  paginationPageSize?: number
  paginationPageSizeSelector?: number[] | boolean
  includeAuditColumns?: boolean
  /**
   * Set to "infinite" together with `datasource` to have the grid fetch each page on demand
   * (AG Grid's Infinite Row Model) instead of paginating client-side over the full `rowData`
   * array already in memory. Omit both (the default) to keep the existing client-side behavior
   * every other caller of this shared component already relies on.
   */
  rowModelType?: RowModelType
  datasource?: IDatasource
  cacheBlockSize?: number
}

export function DataGrid<TData = unknown>({
  rowData,
  columnDefs,
  className,
  height = 520,
  loading = false,
  context,
  defaultColDef,
  gridOptions,
  onGridReady,
  pagination = false,
  paginationPageSize = 10,
  paginationPageSizeSelector = [10, 20, 50, 100],
  includeAuditColumns = true,
  rowModelType,
  datasource,
  cacheBlockSize,
}: DataGridProps<TData>) {
  const { theme } = useTheme()
  const isMerchantUser = useAuthStore((state) => state.user?.userType) === "MERCHANT"

  const agTheme = useMemo(
    () => (theme === "dark" ? agDarkTheme : agLightTheme),
    [theme]
  )

  const mergedDefaultColDef = useMemo<ColDef<TData>>(
    () =>
      ({
        ...commonDefaultColDef,
        ...defaultColDef,
      }) as ColDef<TData>,
    [defaultColDef]
  )

  const mergedColumnDefs = useMemo(
    () =>
      includeAuditColumns ? withAuditColumns(columnDefs) : columnDefs,
    [columnDefs, includeAuditColumns],
  )

  const mergedGridOptions = useMemo<GridOptions<TData>>(
    () => ({
      ...commonGridOptions,
      ...(isMerchantUser
        ? {
            suppressContextMenu: true,
            onCellContextMenu: (params) => {
              params.event?.preventDefault()
            },
          }
        : {}),
      ...gridOptions,
      onGridReady: (event) => {
        gridOptions?.onGridReady?.(event)
        onGridReady?.(event)
      },
    }),
    [gridOptions, onGridReady, isMerchantUser]
  )

  return (
    <div
      className={cn("ag-grid-themed w-full", className)}
      style={{ height }}
      onContextMenu={isMerchantUser ? (event) => event.preventDefault() : undefined}
    >
      <AgGridReact<TData>
        theme={agTheme}
        {...(rowModelType === "infinite"
          ? {
              rowModelType,
              datasource,
              // Deliberately omitted unless the caller explicitly overrides it — cacheBlockSize
              // is an "initial only" AG Grid option (changing it after the grid is created has no
              // effect), but when using Infinite Row Model together with `pagination`, AG Grid
              // keeps the actual per-request fetch size in sync with the *current*
              // paginationPageSize automatically, including when the user changes it via the
              // page-size selector. Explicitly passing cacheBlockSize here (even set to the same
              // initial value) freezes it at that first value forever, which is exactly what
              // broke picking a different page size from the selector.
              ...(cacheBlockSize !== undefined ? { cacheBlockSize } : {}),
            }
          : { rowData: rowData ?? [] })}
        columnDefs={mergedColumnDefs}
        defaultColDef={mergedDefaultColDef}
        context={context}
        loading={loading}
        suppressCellFocus
        animateRows
        pagination={pagination}
        paginationPageSize={paginationPageSize}
        paginationPageSizeSelector={paginationPageSizeSelector}
        enableFilterHandlers={commonGridOptions.enableFilterHandlers}
        gridOptions={mergedGridOptions}
      />
    </div>
  )
}

export type {
  ColDef,
  GridApi,
  GridOptions,
  GridReadyEvent,
  ICellRendererParams,
  RowClickedEvent,
  IsFullWidthRowParams,
} from "ag-grid-community"

export { AgGridReact } from "ag-grid-react"
